/**
 * ===========================================================
 * TopFrame — Discount Progress Bar JS
 * Fichier : assets/tf-discount-bar.js
 *
 * Logique dynamique de la jauge de remise progressive.
 *
 * Fonctionnement :
 * - Lit la configuration depuis les data-attributes du DOM
 *   (valeurs injectées par Liquid depuis l'éditeur de thème)
 * - Interroge l'API Shopify /cart.js pour compter les articles
 * - Met à jour la jauge, le badge et le message en temps réel
 * - Intercepte tous les ajouts/modifications du panier
 * - Compatible avec le snippet floating-atc existant
 * - Aucune dépendance externe (vanilla JS pur)
 *
 * ===========================================================
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────
  // SÉLECTEURS DOM
  // ─────────────────────────────────────────────────────────
  const els = {
    bar:     () => document.getElementById('tf-discount-bar'),
    fill:    () => document.getElementById('tf-discount-fill'),
    track:   () => document.getElementById('tf-discount-track'),
    badge:   () => document.getElementById('tf-discount-badge'),
    counter: () => document.getElementById('tf-cart-qty'),
    message: () => document.getElementById('tf-discount-message'),
  };


  // ─────────────────────────────────────────────────────────
  // LECTURE CONFIG DEPUIS DATA-ATTRIBUTES
  // ─────────────────────────────────────────────────────────
  function getConfig() {
    const barEl = els.bar();
    if (!barEl) return null;

    return {
      tier1: { qty: parseInt(barEl.dataset.tier1Qty) || 2 },
      tier2: { qty: parseInt(barEl.dataset.tier2Qty) || 3 },
      labels: {
        tier1Badge:           barEl.dataset.tier1Badge           || '2 POSTERS = -10%',
        tier2Badge:           barEl.dataset.tier2Badge           || '3 ACHETÉS = -15%',
        tier1UnlockedBadge:   barEl.dataset.tier1UnlockedBadge   || '✅ -10% DÉBLOQUÉ !',
        tier2UnlockedBadge:   barEl.dataset.tier2UnlockedBadge   || '🏆 -15% DÉBLOQUÉ !',
        tier1Message:         barEl.dataset.tier1Message         || 'Ajoutez {remaining} poster(s) pour débloquer -10% 🔥',
        tier2Message:         barEl.dataset.tier2Message         || '-10% débloqué ! Ajoutez encore {remaining} poster(s) pour -15% 🔥',
        tier2UnlockedMessage: barEl.dataset.tier2UnlockedMessage || '🎉 Félicitations ! Vous bénéficiez de -15% sur votre commande !',
      },
    };
  }


  // ─────────────────────────────────────────────────────────
  // LECTURE DU PANIER — TOUS LES ARTICLES CONFONDUS
  // ─────────────────────────────────────────────────────────
  async function getCartItemCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error('Erreur réseau');

      const cart = await response.json();

      // Somme de TOUS les articles (poster A + poster B + poster C = 3)
      return cart.items.reduce((total, item) => total + item.quantity, 0);

    } catch (error) {
      console.warn('[TopFrame Discount Bar] Erreur lecture panier :', error);
      return 0;
    }
  }


  // ─────────────────────────────────────────────────────────
  // MISE À JOUR DE L'AFFICHAGE
  // ─────────────────────────────────────────────────────────
  function updateBar(qty) {
    const config = getConfig();
    if (!config) return;

    const fillEl    = els.fill();
    const trackEl   = els.track();
    const badgeEl   = els.badge();
    const counterEl = els.counter();
    const messageEl = els.message();

    if (!fillEl || !trackEl) return;

    // ── Calcul pourcentage ────────────────────────────────
    const clampedQty = Math.min(qty, config.tier2.qty);
    const percent    = Math.round((clampedQty / config.tier2.qty) * 100);

    // ── Mise à jour compteur et accessibilité ─────────────
    if (counterEl) counterEl.textContent = qty;
    trackEl.setAttribute('aria-valuenow', percent);

    // ── Animation jauge ───────────────────────────────────
    requestAnimationFrame(() => {
      fillEl.style.width = percent + '%';
    });

    // ── Logique paliers ───────────────────────────────────
    if (qty >= config.tier2.qty) {
      // ✅ PALIER 2 ATTEINT
      fillEl.classList.remove('tf-discount-bar__fill--tier1');
      fillEl.classList.add('tf-discount-bar__fill--tier2');
      if (badgeEl)   badgeEl.textContent = config.labels.tier2UnlockedBadge;
      if (messageEl) messageEl.innerHTML = config.labels.tier2UnlockedMessage;

    } else if (qty >= config.tier1.qty) {
      // ✅ PALIER 1 ATTEINT
      fillEl.classList.remove('tf-discount-bar__fill--tier2');
      fillEl.classList.add('tf-discount-bar__fill--tier1');
      const remaining = config.tier2.qty - qty;
      if (badgeEl)   badgeEl.textContent = config.labels.tier1UnlockedBadge;
      if (messageEl) messageEl.innerHTML = config.labels.tier2Message.replace('{remaining}', remaining);

    } else {
      // ❌ AUCUN PALIER
      fillEl.classList.remove('tf-discount-bar__fill--tier1', 'tf-discount-bar__fill--tier2');
      const remaining = config.tier1.qty - qty;
      if (badgeEl)   badgeEl.textContent = config.labels.tier1Badge;
      if (messageEl) messageEl.innerHTML = config.labels.tier1Message.replace('{remaining}', remaining);
    }
  }


  // ─────────────────────────────────────────────────────────
  // RAFRAÎCHISSEMENT COMPLET
  // ─────────────────────────────────────────────────────────
  async function refreshBar() {
    const qty = await getCartItemCount();
    updateBar(qty);
  }


  // ─────────────────────────────────────────────────────────
  // INITIALISATION & ÉCOUTE ÉVÉNEMENTS
  // ─────────────────────────────────────────────────────────
  function init() {
    if (!els.bar()) return;

    // ── Lecture initiale du panier ────────────────────────
    refreshBar();

    // ── Events natifs Dawn / Shopify ──────────────────────
    document.addEventListener('cart:updated', refreshBar);
    document.addEventListener('variant:change', refreshBar);

    // ── TopFrame v1 : mise à jour uniquement quand le panier change ──
    // Dawn publie « cart-update » après chaque ajout, chaque + / − et
    // chaque retrait (fiche produit, barre flottante, tiroir panier).
    // Remplace l'ancienne lecture du panier toutes les 2 secondes et
    // l'interception de tous les appels réseau, coûteuses sur mobile.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshBar);
    }

    // Retour sur la page avec le bouton « précédent » du navigateur :
    // le panier a pu changer entre-temps.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refreshBar();
    });
  }


  // ─────────────────────────────────────────────────────────
  // POINT D'ENTRÉE
  // ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

// ─────────────────────────────────────────────────────────
  // STICKY DISCOUNT BAR — synchronisation avec la jauge principale
  // ─────────────────────────────────────────────────────────

  function initStickyBar() {
    const stickyBar  = document.getElementById('tf-floating-discount');
    const stickyFill = document.getElementById('tf-floating-fill');
    const stickyBadge   = document.getElementById('tf-floating-badge');
    const stickyMessage = document.getElementById('tf-floating-message');
    const mainBar    = document.getElementById('tf-discount-bar');

    // Si les éléments n'existent pas, on sort
    if (!stickyBar || !mainBar) return;

    /**
     * Synchronise le contenu de la sticky bar
     * avec l'état actuel de la jauge principale
     */
    function syncStickyBar() {
      // Copie le fill width depuis la jauge principale
      const mainFill = document.getElementById('tf-discount-fill');
      if (mainFill && stickyFill) {
        stickyFill.style.width = mainFill.style.width;
      }

      // Copie le badge
      const mainBadge = document.getElementById('tf-discount-badge');
      if (mainBadge && stickyBadge) {
        stickyBadge.textContent = mainBadge.textContent;
      }

      // Copie le message
      const mainMessage = document.getElementById('tf-discount-message');
      if (mainMessage && stickyMessage) {
        stickyMessage.textContent = mainMessage.textContent;
      }

      // Sync du compteur
      const mainCounter = document.getElementById('tf-cart-qty');
      const stickyQty   = document.getElementById('tf-floating-qty');
      if (mainCounter && stickyQty) {
        stickyQty.textContent = mainCounter.textContent;
      }

      // Sync du max (tier2 qty)
      const mainBar = document.getElementById('tf-discount-bar');
      const stickyMax = document.getElementById('tf-floating-max');
      if (mainBar && stickyMax) {
        stickyMax.textContent = mainBar.dataset.tier2Qty || '3';
      }
    }

    /**
     * Vérifie si la jauge principale est hors du viewport
     * et affiche/masque la sticky bar en conséquence
     */
    function checkVisibility() {
      // Uniquement sur mobile
      if (window.innerWidth >= 750) {
        stickyBar.classList.remove('is-visible');
        return;
      }

      const rect = mainBar.getBoundingClientRect();
      const isOutOfView = rect.bottom < 0;

      if (isOutOfView) {
        syncStickyBar();
        stickyBar.classList.add('is-visible');
        stickyBar.setAttribute('aria-hidden', 'false');
      } else {
        stickyBar.classList.remove('is-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
      }
    }

    // Écoute du scroll avec requestAnimationFrame pour la performance
    let rafScroll = null;
    window.addEventListener('scroll', () => {
      if (rafScroll) return;
      rafScroll = requestAnimationFrame(() => {
        checkVisibility();
        rafScroll = null;
      });
    }, { passive: true });

    // Sync à chaque mise à jour de la jauge principale
    // On observe les changements de style sur le fill principal
    const mainFillEl = document.getElementById('tf-discount-fill');
    if (mainFillEl) {
      new MutationObserver(syncStickyBar).observe(mainFillEl, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // Init
    checkVisibility();
  }

  // Lancement de la sticky bar après init principale
  initStickyBar();

})();